"use client"

import { notify } from "@/lib/notifications/notify"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { CheckCircle2, Loader2, UserPlus, X } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import Image from "next/image"
import { useToast } from "@/hooks/use-toast"

interface OwnerTeamApprovalCardProps {
  requestId: string
  barberId: string
  barberName: string
  barberAvatar?: string
  shopId: string
  shopName: string
  rentalType?: string
  price?: number
  planType?: string
  paymentIntentId?: string | null
  paymentStatus?: string | null
  onApproved?: () => void
  onRejected?: () => void
  compact?: boolean
}

export function OwnerTeamApprovalCard({
  requestId,
  barberId,
  barberName,
  barberAvatar,
  shopId,
  shopName,
  rentalType = "monthly",
  price = 0,
  planType = "Free Guest",
  paymentIntentId,
  paymentStatus,
  onApproved,
  onRejected,
  compact = false,
}: OwnerTeamApprovalCardProps) {
  const [loading, setLoading] = useState(false)
  const [approved, setApproved] = useState(false)
  const { toast } = useToast()

  const handleAddToTeam = async () => {
    console.log("[v0] handleAddToTeam called for request:", requestId)
    setLoading(true)
    try {
      const supabase = createClient()

      console.log("[v0] Approving reservation request:", requestId)

      // Step 1: Update reservation request status
      const { error: updateError } = await supabase
        .from("reservation_requests")
        .update({
          status: "approved",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId)

      if (updateError) {
        console.error("[v0] Failed to update reservation status:", updateError)
        throw updateError
      }
      console.log("[v0] Reservation status updated to approved")

      // Step 2: Check if barber already exists in shop_barbers
      const { data: existingBarber } = await supabase
        .from("shop_barbers")
        .select("id, status")
        .eq("shop_id", shopId)
        .eq("barber_id", barberId)
        .maybeSingle()

      if (existingBarber) {
        // Update existing record
        const { error: sbUpdateError } = await supabase
          .from("shop_barbers")
          .update({
            status: "active",
            joined_at: new Date().toISOString(),
          })
          .eq("id", existingBarber.id)

        if (sbUpdateError) {
          console.error("[v0] Failed to update shop_barbers:", sbUpdateError)
          throw sbUpdateError
        }
        console.log("[v0] Updated existing shop_barbers record")
      } else {
        // Insert new record
        const { error: sbInsertError } = await supabase.from("shop_barbers").insert({
          shop_id: shopId,
          barber_id: barberId,
          status: "active",
          joined_at: new Date().toISOString(),
        })

        if (sbInsertError) {
          console.error("[v0] Failed to insert shop_barbers:", sbInsertError)
          throw sbInsertError
        }
        console.log("[v0] Inserted new shop_barbers record")
      }

      // Step 3: Create booth rental
      const { data: existingRental } = await supabase
        .from("booth_rentals")
        .select("id")
        .eq("shop_id", shopId)
        .eq("barber_id", barberId)
        .in("status", ["active", "pending_payment"])
        .maybeSingle()

      if (!existingRental) {
        const { error: rentalError } = await supabase.from("booth_rentals").insert({
          shop_id: shopId,
          barber_id: barberId,
          rental_type: rentalType || "monthly",
          price: price || 0,
          status: "active",
          start_date: new Date().toISOString().split("T")[0],
          booked_days: [0, 1, 2, 3, 4, 5, 6],
        })

        if (rentalError) {
          console.error("[v0] Failed to create booth rental:", rentalError)
          // Non-critical, continue anyway
        } else {
          console.log("[v0] Booth rental created")
        }
      }

      // Step 4: Send notification to barber
      const { error: notifError } = await notify({
        recipient: barberId,
        type: "booth_approved",
        shopId: shopId,
        metadata: { shop_id: shopId, shop_name: shopName, reservation_id: requestId },
      })

      if (notifError) {
        console.warn("[v0] Failed to send notification:", notifError)
        // Non-critical, continue anyway
      } else {
        console.log("[v0] Notification sent to barber")
      }

      toast({
        title: "Request Approved!",
        description: `${barberName} has been added to ${shopName}`,
      })

      setApproved(true)
      onApproved?.()
      console.log("[v0] Approval complete!")
    } catch (error: any) {
      console.error("[v0] Failed to approve:", error)
      toast({
        title: "Error",
        description: error.message || "Failed to approve request. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async () => {
    console.log("[v0] handleReject called for request:", requestId)
    setLoading(true)
    try {
      const supabase = createClient()

      const { error: updateError } = await supabase
        .from("reservation_requests")
        .update({
          status: "declined",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId)

      if (updateError) {
        console.error("[v0] Failed to decline:", updateError)
        throw updateError
      }

      await notify({
        recipient: barberId,
        type: "booth_rejected",
        shopId: shopId,
        metadata: { shop_id: shopId, reservation_id: requestId },
      })

      toast({
        title: "Request Declined",
        description: `${barberName}'s request has been declined`,
      })

      onRejected?.()
    } catch (error: any) {
      console.error("[v0] Failed to decline:", error)
      toast({
        title: "Error",
        description: error.message || "Failed to decline request. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  if (approved) {
    return (
      /* Ink, matching the row it replaces. An emerald confirmation card
         appearing where a black Approve button just was is the state change
         announcing itself in a colour the product does not use. */
      <div className={`rounded-xl bg-booth-ink text-white ${compact ? "p-2.5" : "p-4"}`}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-white`} />
          <p className={`${compact ? "text-xs" : "text-sm"} font-medium`}>
            <span className="font-semibold">@{barberName}</span> added
          </p>
        </div>
      </div>
    )
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="relative w-7 h-7 rounded-full overflow-hidden bg-muted flex-shrink-0">
          {barberAvatar ? (
            <Image src={barberAvatar || "/placeholder.svg"} alt={barberName} fill className="object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-booth-ink text-white font-semibold text-[10px]">
              {barberName[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{barberName}</p>
          <p className="text-[10px] text-muted-foreground truncate">
            {rentalType || planType} {price > 0 ? `- $${price}/mo` : ""}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button
            size="sm"
            onClick={handleAddToTeam}
            disabled={loading}
            className="h-7 rounded-full px-3 text-[11px] font-medium bg-booth-ink text-white hover:bg-booth-ink hover:opacity-90"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Approve"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleReject}
            disabled={loading}
            className="h-7 w-7 rounded-full p-0 text-[11px] bg-transparent"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-4">
      <div className="flex items-start gap-4">
        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
          {barberAvatar ? (
            <Image src={barberAvatar || "/placeholder.svg"} alt={barberName} fill className="object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white font-semibold text-sm">
              {barberName[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm mb-3">
            <span className="font-semibold">@{barberName}</span> wants to join{" "}
            <span className="font-semibold">{shopName}</span>
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            {rentalType || planType} {price > 0 && `- $${price}/month`}
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleAddToTeam}
              disabled={loading}
              className="rounded-full bg-booth-ink text-white hover:bg-booth-ink hover:opacity-90"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={handleReject} disabled={loading} className="bg-transparent">
              <X className="w-3 h-3 mr-1" />
              Decline
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
